import {
  afterEveryRender,
  afterNextRender,
  computed,
  DestroyRef,
  inject,
  Injector,
  Service,
  signal
} from '@angular/core'
import { InputModalityDetector } from '@angular/cdk/a11y'
import { focusWithoutTouchRing } from '../../functions/focus-without-touch-ring'

/** The frame that contains the active mobile view. */
export interface PopupSurface {
  readonly presentation: 'page' | 'sheet'
  readonly element: HTMLElement
}

interface PopupEntry {
  readonly close: () => boolean | void
  readonly canClose: () => boolean
  readonly returnFocus: HTMLElement | null
  readonly element?: HTMLElement
  readonly previousOrder?: string
  readonly outlet?: string
  readonly usesScrim: () => boolean
  readonly surface?: () => PopupSurface | undefined
  readonly dismissSheet?: () => boolean
}

/** Keeps dismissal, interaction and focus on the top mobile surface. */
@Service()
export class PopupService {
  private readonly injector = inject(Injector)
  private readonly inputModality = inject(InputModalityDetector)
  private readonly popups = signal<PopupEntry[]>([])
  private readonly backgrounds = signal<readonly HTMLElement[]>([])
  private readonly inertElements = new Map<HTMLElement, boolean>()
  private clickedOpener: HTMLButtonElement | null = null
  private dismissingSheet?: HTMLElement
  private readonly overlayElements = computed(() =>
    this.popups().flatMap(popup => (popup.element ? [popup.element] : []))
  )
  readonly count = computed(() => this.popups().length)
  readonly topCanClose = computed(() => this.popups().at(-1)?.canClose() ?? true)
  readonly topElement = computed(() => this.overlayElements().at(-1))
  readonly hasOverlays = computed(() => this.overlayElements().length > 0)
  readonly topUsesScrim = computed(
    () =>
      this.popups()
        .filter(popup => popup.element)
        .at(-1)
        ?.usesScrim() ?? false
  )
  readonly outletOrder = computed(() => [
    ...new Set(this.popups().flatMap(popup => (popup.outlet ? [popup.outlet] : [])))
  ])

  constructor() {
    const onKeydown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return
      }

      const target = event.target
      const top = this.popups().at(-1)

      // Editors marked as local handle Escape themselves, only while their surface is on top.
      if (
        target instanceof Element &&
        top?.element?.contains(target) &&
        target.closest('[data-overlay-escape="local"]')
      ) {
        return
      }

      if (this.close()) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
    }
    const rememberOpener = (event: MouseEvent): void => {
      const target = event.target
      const button = target instanceof Element ? target.closest<HTMLButtonElement>('button') : null

      // Safari does not focus a tapped button. Remember it without blurring an active input.
      this.clickedOpener = button && !button.disabled && !button.closest('[inert]') ? button : null
    }

    document.addEventListener('keydown', onKeydown, true)
    document.addEventListener('click', rememberOpener, true)
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('keydown', onKeydown, true)
      document.removeEventListener('click', rememberOpener, true)
      this.restoreBackgrounds()
    })
    // Loading views can add background content after a sheet opens.
    afterEveryRender(() => {
      this.clickedOpener = null
      this.restoreBackgrounds()

      const elements = this.overlayElements()
      const top = this.topElement()

      elements.forEach((element, order) => element.style.setProperty('--mobile-overlay-order', String(order)))

      if (top) {
        for (const element of [...this.backgrounds(), ...elements]) {
          this.disableBehind(element, top)
        }
      }
    })
  }

  /**
   * Adds a popup or sheet above the current surface.
   * @param {() => boolean | void} close Requests dismissal. Return false to keep the entry until its owner unregisters it.
   * @param {HTMLElement} element Sheet host for layering and background blocking. Omit for lightweight popups.
   * @param {string | null} outlet Routed page owning the surface. Omit to inherit the active page; null keeps a page independent.
   * @param {() => boolean} usesScrim Whether this surface dims the native status bar.
   * @param {() => PopupSurface | undefined} surface Frame used by entity details opened from this overlay.
   * @param {() => boolean} dismissSheet Starts downward dismissal, including for a retained sheet below its detail pages.
   * @param {() => boolean} canClose Whether the owner can accept a close request, for example after a pending save.
   * @returns {() => void} Cleanup that unregisters the surface and restores focus.
   */
  register(
    close: () => boolean | void,
    element?: HTMLElement,
    outlet?: string | null,
    usesScrim: () => boolean = () => true,
    surface?: () => PopupSurface | undefined,
    dismissSheet?: () => boolean,
    canClose: () => boolean = () => true
  ): () => void {
    const activeElement = this.clickedOpener ?? document.activeElement

    this.clickedOpener = null

    const popup: PopupEntry = {
      close,
      canClose,
      returnFocus: activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null,
      element,
      previousOrder: element?.style.getPropertyValue('--mobile-overlay-order'),
      outlet: outlet === null ? undefined : (outlet ?? this.popups().at(-1)?.outlet),
      usesScrim,
      surface,
      dismissSheet
    }

    this.popups.update(popups => [...popups, popup])

    return () => this.unregister(popup)
  }

  /**
   * Returns the frame of the current overlay, excluding lightweight menus.
   * @returns {PopupSurface | undefined}
   */
  getActiveSurface(): PopupSurface | undefined {
    return this.popups()
      .filter(popup => popup.element)
      .at(-1)
      ?.surface?.()
  }

  /**
   * Dismisses the detail pages and their sheet in order.
   * @param {HTMLElement} frame Retained sheet frame.
   */
  dismissSheet(frame: HTMLElement): void {
    this.dismissingSheet = frame
    this.dismissNextSheetSurface()
  }

  /**
   * Returns the panels that move together when a retained sheet is pulled.
   * @param {HTMLElement} frame Retained sheet frame.
   * @returns {HTMLElement[]}
   */
  sheetPanels(frame: HTMLElement): HTMLElement[] {
    return this.popups()
      .filter(popup => popup.surface?.()?.element === frame)
      .flatMap(popup => {
        // Inline pages own their clipping frame; the base sheet keeps its backdrop stationary.
        const panel = popup.element === frame.parentElement ? frame : popup.element

        return panel ? [panel] : []
      })
  }

  /** Waits for each owner to remove its view before closing the next view in the sheet. */
  private dismissNextSheetSurface(): void {
    const popup = this.popups()
      .filter(entry => entry.surface?.()?.element === this.dismissingSheet)
      .at(-1)

    if (!this.dismissingSheet || !popup?.dismissSheet || !popup.dismissSheet()) {
      this.dismissingSheet = undefined
    }
  }

  /**
   * Checks the full stack, including lightweight popups.
   * @param {HTMLElement} element Surface to check.
   * @returns {boolean}
   */
  isCovered(element: HTMLElement): boolean {
    const top = this.popups().at(-1)
    return !!top && top.element !== element
  }

  /**
   * Brings a retained page and its nested selectors forward.
   * @param {string} outlet Page outlet to activate.
   */
  activate(outlet: string): void {
    const popups = this.popups()
    const page = popups.find(popup => popup.outlet === outlet && popup.element)?.element
    const top = this.topElement()

    if (!page || !top || popups.at(-1)?.outlet === outlet || page.contains(top)) {
      return
    }

    const belongsToPage = (popup: PopupEntry): boolean =>
      popup.outlet === outlet || (!!popup.element && page.contains(popup.element))

    this.popups.set([...popups.filter(popup => !belongsToPage(popup)), ...popups.filter(belongsToPage)])

    const active = this.popups().at(-1)

    afterNextRender(
      () => {
        if (this.popups().at(-1) === active) {
          this.topElement()?.querySelector<HTMLElement>('[role="dialog"]')?.focus({ preventScroll: true })
        }
      },
      { injector: this.injector }
    )
  }

  /**
   * Registers app content that remains mounted below sheets.
   * @param {HTMLElement} element Background region, which may contain the active sheet.
   * @returns {() => void} Cleanup for the background view.
   */
  registerBackground(element: HTMLElement): () => void {
    this.backgrounds.update(elements => [...elements, element])
    return () => this.backgrounds.update(elements => elements.filter(item => item !== element))
  }

  /**
   * Handles Back for the top overlay.
   * @returns {boolean} Whether an overlay handled Back.
   */
  close(): boolean {
    const popup = this.popups().at(-1)

    if (!popup) {
      return false
    }

    if (popup.close() !== false) {
      this.unregister(popup)
    }

    return true
  }

  /**
   * Removes only this overlay and restores focus after the previous sheet resumes.
   * @param {PopupEntry} popup Overlay to remove.
   */
  private unregister(popup: PopupEntry): void {
    const popups = this.popups()

    if (!popups.includes(popup)) {
      return
    }

    const wasTop = popups.at(-1) === popup

    if (popup.element) {
      if (popup.previousOrder) {
        popup.element.style.setProperty('--mobile-overlay-order', popup.previousOrder)
      } else {
        popup.element.style.removeProperty('--mobile-overlay-order')
      }
    }

    this.popups.set(popups.filter(entry => entry !== popup))

    if (this.dismissingSheet) {
      // Routed owners unregister during navigation. Wait for that navigation to finish rendering.
      afterNextRender(() => this.dismissNextSheetSurface(), { injector: this.injector })
    }

    if (!wasTop) {
      return
    }

    const previous = this.popups().at(-1)

    afterNextRender(
      () => {
        const target = popup.returnFocus
        const active = document.activeElement

        if (this.popups().at(-1) !== previous) {
          return
        }

        if (
          !popup.element &&
          active !== document.body &&
          !(active instanceof HTMLElement && active.closest('[inert]'))
        ) {
          return
        }

        const modality = this.inputModality.mostRecentModality

        if (target?.isConnected && !target.closest('[inert]')) {
          focusWithoutTouchRing(target, modality)
        } else if (popup.element) {
          const fallback =
            this.topElement()?.querySelector<HTMLElement>('[role="dialog"]') ??
            document.querySelector<HTMLElement>('[data-overlay-background][tabindex]')

          if (fallback) {
            focusWithoutTouchRing(fallback, modality)
          }
        }
      },
      { injector: this.injector }
    )
  }

  /**
   * Disables sibling branches while keeping a nested sheet usable.
   * @param {HTMLElement} element Background or covered sheet host.
   * @param {HTMLElement} top Active sheet host.
   */
  private disableBehind(element: HTMLElement, top: HTMLElement): void {
    if (element === top) {
      return
    }

    if (element.contains(top)) {
      for (const child of Array.from(element.children)) {
        if (child instanceof HTMLElement) {
          this.disableBehind(child, top)
        }
      }
    } else {
      if (!this.inertElements.has(element)) {
        this.inertElements.set(element, element.inert)
      }

      element.inert = true
    }
  }

  /** Restores only the inert values changed by this service. */
  private restoreBackgrounds(): void {
    for (const [element, inert] of this.inertElements) {
      element.inert = inert
    }

    this.inertElements.clear()
  }
}
